import type { DailyAggregate } from '@etiquetachef/types';

/**
 * O repasse mensal mais recente do período: o último dia da série que tem `partnerRemit`.
 *
 * Origem: #743 — 2026-09-25 17:32 +0200 (F8 da #679, ativação pelo parceiro).
 *
 * Por quê: `partnerRemit` é RETRATO do dia (o que os tenants PARTNER ativos repassam por mês),
 * não fluxo — somar os dias contaria o mesmo repasse N vezes; o certo é o último disponível.
 * Dia anterior à F8 não tem o campo (é `optional` no schema), e "sem dado" é diferente de
 * "zero": série sem nenhum dia com o campo devolve `null`, e o card mostra "sem dado".
 *
 * Usado por: `PartnerRemittanceCard` (`apps/admin`, dashboard financeiro).
 *
 * Dados: `/aggregations/daily/days/{yyyymmdd}.partnerRemit` — `{ byCurrency: {BRL,USD,EUR}:
 * centavos, activeTenants }`, escrito por `aggregateDailyMetrics` (Functions); schema
 * `DailyAggregateSchema` em `packages/types/src/aggregations.ts`.
 *
 * Histórico:
 *  - 2026-10-10 14:40 +0200 — (#798) caminho corrigido (faltava `days`) e cabeçalho padrão (#796).
 */
export function latestPartnerRemit(
  series: readonly DailyAggregate[],
): NonNullable<DailyAggregate['partnerRemit']> | null {
  for (let i = series.length - 1; i >= 0; i -= 1) {
    const remit = series[i]?.partnerRemit;
    if (remit) return remit;
  }
  return null;
}

type SplitCommissionTotals = Record<'BRL' | 'USD' | 'EUR', { split: number; connect: number; payable: number }>;

/**
 * Comissão de parceiro DIRECT SOMADA no período, por moeda e forma (`split`/`connect`/`payable`).
 *
 * Origem: #753 — 2026-09-27 19:33 +0200 (A4 do Asaas, cadastro do parceiro e extrato).
 *
 * Por quê: ao contrário do repasse (retrato do dia — ver `latestPartnerRemit`),
 * `partnerSplitCommission` é o FLUXO dos pagamentos de cada dia, então o período é a soma.
 * Dia anterior à A4 não tem o campo; série inteira sem ele é "sem dado" (`null`), não zero —
 * `seen` existe só para essa distinção. Valores em centavos; é a parte do bruto que não é
 * nossa, e o MRR segue pelo bruto (decisão da #748).
 *
 * Usado por: `PartnerRemittanceCard` (`apps/admin`, dashboard financeiro).
 *
 * Dados: `/aggregations/daily/days/{yyyymmdd}.partnerSplitCommission`, schema
 * `DailyAggregateSchema` em `packages/types/src/aggregations.ts`.
 *
 * Histórico:
 *  - 2026-10-10 14:40 +0200 — (#798) caminho corrigido (faltava `days`) e cabeçalho padrão (#796).
 */
export function sumPartnerSplitCommission(series: readonly DailyAggregate[]): SplitCommissionTotals | null {
  let seen = false;
  const zero = (): { split: number; connect: number; payable: number } => ({ split: 0, connect: 0, payable: 0 });
  const out: SplitCommissionTotals = { BRL: zero(), USD: zero(), EUR: zero() };
  for (const d of series) {
    const c = d.partnerSplitCommission;
    if (!c) continue;
    seen = true;
    for (const cur of ['BRL', 'USD', 'EUR'] as const) {
      out[cur].split += c.byCurrency[cur].split;
      out[cur].connect += c.byCurrency[cur].connect;
      out[cur].payable += c.byCurrency[cur].payable;
    }
  }
  return seen ? out : null;
}
